import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useJsonLd } from '@/hooks/useJsonLd';
import { SITE_URL, SITE_NAME, FAVICON_URL } from '@/lib/site';
import { articles } from '@/mocks/article-content';
import { mainSiteUrl, categories } from '@/mocks/categories';

const heroImages: Record<string, string> = {
  'what-is-usfans-spreadsheet': 'https://readdy.ai/api/search-image?query=An%20organized%20spreadsheet%20and%20product%20catalog%20flat%20lay%20with%20sneakers%20and%20folded%20clothing%20on%20a%20clean%20warm%20beige%20desk%2C%20soft%20natural%20lighting%2C%20minimal%20editorial%20style%2C%20calm%20harmonious%20composition&width=1200&height=600&seq=article-whatis-01&orientation=landscape',
  'complete-usfans-spreadsheet-guide': 'https://readdy.ai/api/search-image?query=A%20neatly%20organized%20binder%20and%20color%20coded%20spreadsheet%20next%20to%20a%20laptop%20and%20shopping%20bags%20on%20a%20soft%20sage%20green%20desk%2C%20warm%20natural%20light%2C%20minimal%20editorial%20flat%20lay%2C%20calm%20productive%20mood&width=1200&height=600&seq=article-complete-01&orientation=landscape',
  'usfans-spreadsheet-2026': 'https://readdy.ai/api/search-image?query=A%20modern%202026%20calendar%20and%20a%20tablet%20showing%20product%20cards%20beside%20a%20small%20plant%20on%20a%20clean%20blush%20pink%20surface%2C%20soft%20daylight%2C%20minimal%20editorial%20flat%20lay%2C%20fresh%20forward-looking%20mood&width=1200&height=600&seq=article-2026-01&orientation=landscape',
  'usfans-review': 'https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20over%20a%20stack%20of%20product%20cards%20with%20five%20star%20icons%20on%20a%20warm%20peach%20surface%2C%20soft%20studio%20light%2C%20minimal%20review%20concept%20illustration%2C%20friendly%20trustworthy%20mood&width=1200&height=600&seq=article-review-01&orientation=landscape',
  'usfans-shipping-cost': 'https://readdy.ai/api/search-image?query=Kraft%20shipping%20boxes%2C%20a%20calculator%20and%20a%20notepad%20with%20price%20notes%20on%20a%20pale%20blue%20grey%20desk%2C%20soft%20natural%20light%2C%20minimal%20logistics%20flat%20lay%2C%20clear%20organized%20mood&width=1200&height=600&seq=article-cost-01&orientation=landscape',
  'usfans-shipping-guide': 'https://readdy.ai/api/search-image?query=A%20delivery%20van%20toy%20and%20neatly%20taped%20cardboard%20boxes%20with%20a%20route%20map%20on%20a%20clean%20cream%20surface%2C%20warm%20ambient%20light%2C%20minimal%20shipping%20flat%20lay%20photography%2C%20calm%20efficient%20mood&width=1200&height=600&seq=article-shipping-01&orientation=landscape',
  'usfans-warehouse-guide': 'https://readdy.ai/api/search-image?query=A%20small%20warehouse%20interior%20with%20organized%20shelving%20and%20cardboard%20boxes%20on%20a%20warm%20sand%20tone%2C%20soft%20industrial%20lighting%2C%20minimal%20editorial%20illustration%2C%20tidy%20structured%20mood&width=1200&height=600&seq=article-warehouse-01&orientation=landscape',
  'usfans-coupon-discount-code': 'https://readdy.ai/api/search-image?query=A%20pair%20of%20scissors%20and%20a%20discount%20tag%20beside%20a%20gift%20box%20on%20a%20soft%20rose%20surface%2C%20warm%20natural%20light%2C%20minimal%20promotional%20flat%20lay%2C%20inviting%20but%20honest%20mood&width=1200&height=600&seq=article-coupon-01&orientation=landscape',
  'usfans-seller-guide': 'https://readdy.ai/api/search-image?query=A%20storefront%20icon%20and%20connected%20profile%20cards%20with%20a%20small%20magnifying%20glass%20on%20a%20soft%20sage%20green%20surface%2C%20soft%20daylight%2C%20minimal%20seller%20research%20illustration%2C%20trustworthy%20mood&width=1200&height=600&seq=article-seller-01&orientation=landscape',
  'usfans-product-research-guide': 'https://readdy.ai/api/search-image?query=A%20tidy%20desk%20with%20product%20swatches%2C%20a%20notepad%20and%20a%20tablet%20showing%20catalog%20images%20on%20a%20warm%20beige%20surface%2C%20soft%20natural%20light%2C%20minimal%20research%20flat%20lay%2C%20focused%20calm%20mood&width=1200&height=600&seq=article-product-01&orientation=landscape',
  'usfans-buying-guide': 'https://readdy.ai/api/search-image?query=A%20shopping%20cart%20with%20a%20checklist%20and%20a%20few%20folded%20clothing%20items%20on%20a%20soft%20cream%20surface%2C%20warm%20ambient%20light%2C%20minimal%20buying%20guide%20flat%20lay%2C%20reassuring%20organized%20mood&width=1200&height=600&seq=article-buying-01&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0] as 'en' | 'pl' | 'es' | 'fr';

  const found = articles.find((a) => a.slug === slug) || articles[0];
  const meta = found.meta[lang] || found.meta.en;
  const body = found.body[lang] || found.body.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const articleUrl = `${SITE_URL}/article/${found.slug}`;
  useJsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${articleUrl}#article`,
        headline: meta.title,
        description: meta.description,
        image: heroImages[found.slug],
        author: { '@type': 'Organization', name: found.author },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: { '@type': 'ImageObject', url: FAVICON_URL },
        },
        datePublished: found.publishedDate,
        dateModified: found.updatedDate,
        mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
        inLanguage: lang,
      },
      {
        '@type': 'FAQPage',
        '@id': `${articleUrl}#faq`,
        mainEntity: body.faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="mx-auto max-w-4xl px-4 py-10 md:px-6 md:py-14">
        <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
          <Link to="/" className="transition-colors hover:text-primary-600">
            {t('brand')}
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-700">{meta.title}</span>
        </nav>

        <article>
          <header className="mt-6">
            <span className="inline-flex items-center rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
              {t(found.categoryKey)}
            </span>
            <h1 className="mt-4 text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
              {meta.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center text-primary-600">
                  <i className="ri-user-line text-sm" />
                </span>
                {found.author}
              </span>
              <span>{t('common.updated')} {found.updatedDate}</span>
              <span>{found.readingTime} {t('guides.minRead')}</span>
            </div>
          </header>

          <div className="mt-8">
            <img
              src={heroImages[found.slug] || 'https://readdy.ai/api/search-image?query=Organized%20spreadsheet%20and%20product%20catalog%20flat%20lay%20with%20sneakers%20and%20clothing%20items%20on%20a%20clean%20warm%20beige%20desk%20background%2C%20soft%20natural%20lighting%2C%20minimal%20editorial%20style%2C%20high%20detail%2C%20calm%20harmonious%20composition&width=1200&height=600&seq=article-hero-01&orientation=landscape'}
              alt={meta.title}
              className="w-full rounded-lg object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {body.intro.map((p, i) => (
              <p key={i} className="text-base leading-relaxed text-foreground-700">
                {p}
              </p>
            ))}
          </div>

          {body.sections.map((section, idx) => (
            <section key={idx} className="mt-10">
              <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">{section.heading}</h2>
              <div className="mt-4 space-y-4">
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-foreground-700">
                    {p}
                  </p>
                ))}
                {section.bullets && (
                  <ul className="space-y-2.5">
                    {section.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-3 text-base leading-relaxed text-foreground-700">
                        <span className="mt-1.5 flex h-5 w-5 items-center justify-center text-primary-600">
                          <i className="ri-checkbox-circle-line text-base" />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          ))}

          <section className="mt-10">
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">
              {t('article.faqTitle')}
            </h2>
            <div className="mt-4 space-y-3">
              {body.faqs.map((f) => (
                <details key={f.question} className="group rounded-lg border border-background-200 bg-background-50">
                  <summary className="flex cursor-pointer items-center justify-between gap-3 px-5 py-4 text-sm font-semibold text-foreground-900">
                    {f.question}
                    <span className="flex h-5 w-5 items-center justify-center text-foreground-500 transition-transform group-open:rotate-180">
                      <i className="ri-arrow-down-s-line text-base" />
                    </span>
                  </summary>
                  <p className="px-5 pb-4 text-sm leading-relaxed text-foreground-600">{f.answer}</p>
                </details>
              ))}
            </div>
          </section>

          <p className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6 text-base leading-relaxed text-foreground-700">
            {body.conclusion}
          </p>
        </article>

        <section className="mt-12 rounded-lg bg-accent-50 p-6">
          <h2 className="text-lg font-bold text-foreground-950">{t('final.title')}</h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t('final.copy')}</p>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-shopping-cart-line text-sm" />
              {t('hero.secondaryCta')}
            </a>
            <Link
              to="/"
              className="flex items-center justify-center gap-2 rounded-md border border-accent-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-accent-900 transition-colors hover:bg-accent-100 whitespace-nowrap"
            >
              <i className="ri-home-line text-sm" />
              {t('common.backHome')}
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-lg font-bold text-foreground-950">{t('category.browse')}</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {categories.slice(0, 8).map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400"
              >
                <span className="flex h-6 w-6 items-center justify-center text-primary-600">
                  <i className={`${cat.icon} text-sm`} />
                </span>
                {t(cat.nameKey)}
              </a>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}